// src/hooks/useUserManagement.ts
// ✅ LOGGING: Complete migration to centralized logging system

import { useState, useEffect, useCallback } from 'react';
import { userAPI, UserStats } from '../services/userAPI';
import { User, UserFilters, CreateUserRequest, UpdateUserRequest } from '../types/user';
import { logger } from '../utils/logger';

// ✅ LOGGING: Create dedicated logger for User Management hook
const userManagementLogger = logger.createServiceLogger('UserManagement');

export interface UseUserManagementState {
  users: User[];
  loading: boolean;
  error: string | null;
  totalPages: number;
  currentPage: number;
  selectedUsers: string[];
  userStats: UserStats;
}

export interface UseUserManagementActions {
  createUser: (userData: CreateUserRequest) => Promise<void>;
  updateUser: (userId: string, userData: UpdateUserRequest) => Promise<void>;
  deleteUser: (userId: string, reason?: string) => Promise<void>;
  updateUserStatus: (userId: string, status: string, reason?: string) => Promise<void>;
  loadUsers: (page?: number) => Promise<void>;
  setPage: (page: number) => void;
  setSearchTerm: (term: string) => void;
  clearError: () => void;
  setSelectedUsers: (userIds: string[]) => void;
  bulkUpdateStatus: (userIds: string[], status: string, reason?: string) => Promise<{ updated: string[]; errors: Array<{ userId: string; error: string }> }>;
}

export type UseUserManagementReturn = UseUserManagementState & UseUserManagementActions;

// ✅ FIX: Global deduplication for React StrictMode
let globalUserLoadPromise: Promise<any> | null = null;
let globalStatsLoadPromise: Promise<any> | null = null;
let globalUserLoadKey = '';
let globalStatsLoadKey = '';

export const useUserManagement = (): UseUserManagementReturn => {
  // State management
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [userStats, setUserStats] = useState<UserStats>({
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    total: 0,
    active: 0,
    inactive: 0,
    byRank: {},
    byAssignment: {},
    byDepartment: {}
  });

  const pageSize = 50;

  // Load users with current filters
  const loadUsers = useCallback(async (page: number = currentPage) => {
    const timer = logger.startTimer('UserManagement', 'loadUsers');
    const currentKey = `users-${page}-${searchTerm}-${pageSize}`;
    
    // ✅ FIX: Prevent duplicate calls during React StrictMode
    if (globalUserLoadPromise && globalUserLoadKey === currentKey) {
      userManagementLogger.debug('Deduplicating user load request', {
        page,
        searchTerm,
        reason: 'Already loading'
      });
      return globalUserLoadPromise;
    }

    setLoading(true);
    setError(null);
    
    const loadingPromise = (async () => {
      try {
        const filters: UserFilters = {};
        if (searchTerm) {
          filters.search = searchTerm;
        }

        userManagementLogger.info('Loading users with filters', {
          page,
          searchTerm,
          pageSize,
          hasFilters: !!searchTerm,
          timestamp: new Date().toISOString()
        });
        
        const response = await userAPI.getUsers(page, pageSize, filters);
        
        userManagementLogger.info('Users loaded successfully', {
          userCount: response.users.length,
          totalCount: response.totalCount,
          currentPage: page,
          totalPages: Math.ceil(response.totalCount / pageSize),
          searchTerm: searchTerm || null
        });
        
        setUsers(response.users);
        setCurrentPage(page);
        setTotalPages(Math.ceil(response.totalCount / pageSize));
        
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load users';
        userManagementLogger.error('Error loading users', {
          page,
          searchTerm,
          pageSize,
          error: errorMessage
        }, err instanceof Error ? err : undefined);
        
        setError(errorMessage);
        setUsers([]);
      } finally {
        setLoading(false);
        timer(); // Complete timing
        // Clear global loading state
        if (globalUserLoadKey === currentKey) {
          globalUserLoadPromise = null;
          globalUserLoadKey = '';
        }
      }
    })();

    // Set global loading state
    globalUserLoadPromise = loadingPromise;
    globalUserLoadKey = currentKey;

    return loadingPromise;
  }, [currentPage, searchTerm, pageSize]);

  // Load user statistics
  const loadUserStats = useCallback(async () => {
    const timer = logger.startTimer('UserManagement', 'loadUserStats');
    const currentKey = 'stats-load';
    
    // ✅ FIX: Prevent duplicate stats loading
    if (globalStatsLoadPromise && globalStatsLoadKey === currentKey) {
      userManagementLogger.debug('Deduplicating stats load request', {
        reason: 'Already loading'
      });
      return globalStatsLoadPromise;
    }

    const loadingPromise = (async () => {
      try {
        userManagementLogger.info('Loading user statistics');
        const stats = await userAPI.getUserStats();
        
        userManagementLogger.info('User statistics loaded successfully', {
          totalUsers: stats.totalUsers,
          activeUsers: stats.activeUsers,
          inactiveUsers: stats.inactiveUsers,
          rankDistribution: Object.keys(stats.byRank).length,
          assignmentDistribution: Object.keys(stats.byAssignment).length,
          departmentDistribution: Object.keys(stats.byDepartment).length
        });
        
        setUserStats(stats);
      } catch (err) {
        userManagementLogger.error('Error loading user statistics', {
          error: err instanceof Error ? err.message : 'Unknown error'
        }, err instanceof Error ? err : undefined);
        
        // Don't set error state for stats failure - it's not critical
      } finally {
        timer(); // Complete timing
        // Clear global loading state
        if (globalStatsLoadKey === currentKey) {
          globalStatsLoadPromise = null;
          globalStatsLoadKey = '';
        }
      }
    })();

    // Set global loading state
    globalStatsLoadPromise = loadingPromise;
    globalStatsLoadKey = currentKey;

    return loadingPromise;
  }, []);

  // Create new user
  const createUser = async (userData: CreateUserRequest): Promise<void> => {
    const timer = logger.startTimer('UserManagement', 'createUser');
    setLoading(true);
    setError(null);
    
    try {
      userManagementLogger.info('Creating new user', {
        employeeNumber: userData.employeeNumber,
        firstName: userData.firstName,
        lastName: userData.lastName,
        rank: userData.rank,
        department: userData.department,
        email: userData.email
      });
      
      await userAPI.createUser(userData);
      
      userManagementLogger.info('User created successfully', {
        employeeNumber: userData.employeeNumber,
        fullName: `${userData.firstName} ${userData.lastName}`
      });
      
      // Reload current page and stats
      await Promise.all([
        loadUsers(currentPage),
        loadUserStats()
      ]);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create user';
      userManagementLogger.error('Error creating user', {
        employeeNumber: userData.employeeNumber,
        firstName: userData.firstName,
        lastName: userData.lastName,
        error: errorMessage
      }, err instanceof Error ? err : undefined);
      
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
      timer(); // Complete timing
    }
  };

  // Update existing user
  const updateUser = async (userId: string, userData: UpdateUserRequest): Promise<void> => {
    const timer = logger.startTimer('UserManagement', 'updateUser');
    setLoading(true);
    setError(null);
    
    try {
      userManagementLogger.info('Updating user', {
        userId,
        updateFields: Object.keys(userData),
        hasFirstName: !!userData.firstName,
        hasLastName: !!userData.lastName,
        hasRank: !!userData.rank,
        hasDepartment: !!userData.department,
        hasEmail: !!userData.email
      });
      
      await userAPI.updateUser(userId, userData);
      
      userManagementLogger.info('User updated successfully', {
        userId,
        updateFieldCount: Object.keys(userData).length
      });
      
      // Reload current page and stats
      await Promise.all([
        loadUsers(currentPage),
        loadUserStats()
      ]);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update user';
      userManagementLogger.error('Error updating user', {
        userId,
        updateFields: Object.keys(userData),
        error: errorMessage
      }, err instanceof Error ? err : undefined);
      
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
      timer(); // Complete timing
    }
  };

  // Delete user (soft delete)
  const deleteUser = async (userId: string, reason?: string): Promise<void> => {
    const timer = logger.startTimer('UserManagement', 'deleteUser');
    setLoading(true);
    setError(null);
    
    try {
      userManagementLogger.info('Deleting user', {
        userId,
        reason: reason || 'No reason provided',
        softDelete: true
      });
      
      await userAPI.deleteUser(userId, reason);
      
      userManagementLogger.info('User deleted successfully', {
        userId,
        reason: reason || 'No reason provided'
      });
      
      // Remove from selected users if present
      setSelectedUsers(prev => prev.filter(id => id !== userId));
      
      // Reload current page and stats
      await Promise.all([
        loadUsers(currentPage),
        loadUserStats()
      ]);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete user';
      userManagementLogger.error('Error deleting user', {
        userId,
        reason: reason || 'No reason provided',
        error: errorMessage
      }, err instanceof Error ? err : undefined);
      
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
      timer(); // Complete timing
    }
  };

  // Update user status
  const updateUserStatus = async (userId: string, status: string, reason?: string): Promise<void> => {
    const timer = logger.startTimer('UserManagement', 'updateUserStatus');
    setLoading(true);
    setError(null);
    
    try {
      userManagementLogger.info('Updating user status', {
        userId,
        newStatus: status,
        reason: reason || 'No reason provided',
        timestamp: new Date().toISOString()
      });
      
      await userAPI.updateUserStatus(userId, status, reason);
      
      userManagementLogger.info('User status updated successfully', {
        userId,
        newStatus: status,
        reason: reason || 'No reason provided'
      });
      
      // Reload current page and stats
      await Promise.all([
        loadUsers(currentPage),
        loadUserStats()
      ]);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update user status';
      userManagementLogger.error('Error updating user status', {
        userId,
        targetStatus: status,
        reason: reason || 'No reason provided',
        error: errorMessage
      }, err instanceof Error ? err : undefined);
      
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
      timer(); // Complete timing
    }
  };

  // Bulk update user statuses
  const bulkUpdateStatus = async (
    userIds: string[], 
    status: string, 
    reason?: string
  ): Promise<{ updated: string[]; errors: Array<{ userId: string; error: string }> }> => {
    const timer = logger.startTimer('UserManagement', 'bulkUpdateStatus');
    setLoading(true);
    setError(null);
    
    try {
      userManagementLogger.info('Starting bulk user status update', {
        userCount: userIds.length,
        targetStatus: status,
        reason: reason || 'No reason provided',
        userIds: userIds.slice(0, 5), // Log first 5 IDs for debugging
        timestamp: new Date().toISOString()
      });
      
      const result = await userAPI.bulkUpdateStatus(userIds, status, reason);
      
      userManagementLogger.info('Bulk user status update completed', {
        totalUsers: userIds.length,
        successfulUpdates: result.updated.length,
        failedUpdates: result.errors.length,
        targetStatus: status,
        successRate: `${((result.updated.length / userIds.length) * 100).toFixed(1)}%`,
        errors: result.errors.map(e => ({ userId: e.userId, error: e.error }))
      });
      
      // Clear selected users
      setSelectedUsers([]);
      
      // Reload current page and stats
      await Promise.all([
        loadUsers(currentPage),
        loadUserStats()
      ]);
      
      return result;
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update users';
      userManagementLogger.error('Error in bulk user status update', {
        userCount: userIds.length,
        targetStatus: status,
        reason: reason || 'No reason provided',
        error: errorMessage
      }, err instanceof Error ? err : undefined);
      
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
      timer(); // Complete timing
    }
  };

  // Set page number
  const setPage = (page: number): void => {
    userManagementLogger.debug('Page change requested', {
      currentPage,
      newPage: page,
      totalPages
    });
    
    setCurrentPage(page);
    loadUsers(page);
  };

  // Set search term (debounced effect will trigger reload)
  const setSearchTermHandler = (term: string): void => {
    userManagementLogger.debug('Search term updated', {
      previousTerm: searchTerm,
      newTerm: term,
      termLength: term.length
    });
    
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Clear error
  const clearError = (): void => {
    userManagementLogger.debug('Error state cleared');
    setError(null);
  };

  // ✅ FIX: Initial load and search term debouncing - prevent duplicate dependency calls
  useEffect(() => {
    userManagementLogger.debug('Effect triggered for user data reload', {
      trigger: 'searchTerm change',
      searchTerm,
      hasSearchTerm: !!searchTerm
    });
    
    loadUsers(1);
  }, [searchTerm]); // Only depend on searchTerm

  // ✅ FIX: Load stats on mount only once
  useEffect(() => {
    userManagementLogger.debug('Effect triggered for initial stats load', {
      trigger: 'component mount'
    });
    
    loadUserStats();
  }, []); // Empty dependency array - only run on mount

  return {
    // State
    users,
    loading,
    error,
    totalPages,
    currentPage,
    selectedUsers,
    userStats,
    
    // Actions
    createUser,
    updateUser,
    deleteUser,
    updateUserStatus,
    loadUsers,
    setPage,
    setSearchTerm: setSearchTermHandler,
    clearError,
    setSelectedUsers,
    bulkUpdateStatus
  };
};